import React, { Fragment, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, Row, Col, Form, InputGroup } from 'react-bootstrap';

import ListInContent from 'components/common-sfims/ListInContent';
import Pagination from 'components/common-sfims/pagination/paging';
import Select from 'components/common-sfims/form/Select';
import columns from 'components/pages/haccp/standardInfo/employee/columns';
import excelColumns from 'components/pages/haccp/standardInfo/employee/excelColumns';

// function
import {
  array2ValueAndText,
  utc2tz,
  datas2SfshExcelJSON
} from 'function/convert';
import { downloadExcelByJSON } from 'function/file';
// types
import { RESPONSE_CODE, ROUTER_PATH } from 'types/const';
import {
  HACCP_AUTH_TYPE,
  HACCP_AUTH_TYPE_SELECT,
  USE_YN_SELECT
} from 'types/type';
// utils
import { clean, pluck } from 'utils/array';
import comma from 'utils/format/comma';
import numberFrom from 'utils/number/from';
import format from 'utils/string/format';
// api
import { requestList as request } from 'api/admin';
import { factory } from 'api/haccp/commonApi';
// hooks
import useHistoryState from 'hooks/useHistoryState';

/**
 * 기준 정보 > 사원 관리 - 목록
 */
const List = () => {
  /* variable */
  const title = '사원 목록';

  /* router */
  const history = useHistory();
  const { state } = useLocation();

  /* state */
  // 목록
  const [datas, setDatas] = useState([]);
  // 로딩 여부
  const [isLoading, setIsLoading] = useState(true);
  // 메시지
  const [message, setMessage] = useState({});
  // 공장 목록
  const [plants, setPlants] = useState([]);
  // 총 데이터 수
  const [total, setTotal] = useState(0);

  /* 조회조건 */

  // 선택한 담당 공장
  const [factoryCodeSeq, setFactoryCodeSeq] = useHistoryState(
    undefined,
    'factoryCodeSeq'
  );
  // 선택한 결재권한
  const [haccpAuth, setHaccpAuth] = useHistoryState(undefined, 'haccpAuth');
  // 선택한 구분
  const [type, setType] = useHistoryState(0, 'type');
  // ID 검색어
  const [userId, setUserId] = useHistoryState(undefined, 'userId');
  // ID 검색어
  const [userNm, setUserNm] = useHistoryState(undefined, 'userNm');
  // 선택한 사용유무
  const [useYn, setUseYn] = useHistoryState(undefined, 'useYn');
  // 사원 목록 조회 요청 데이터
  const [params, setParams] = useHistoryState(
    {
      page: 0,
      size: 12,
      ...state
    },
    'params'
  );

  /* watch */
  useEffect(() => {
    requestPlant();
  }, []);
  useEffect(() => {
    // 담당 공장을 선택한 경우 결재권한을 관리자로 선택 시 담당 공장을 전체로 설정
    if (haccpAuth === HACCP_AUTH_TYPE.ADMIN && factoryCodeSeq) {
      setFactoryCodeSeq('');
    }
  }, [haccpAuth]);
  useEffect(() => {
    requestList();
  }, [params]);

  /* method */
  // 사원 목록 조회 요청
  const requestList = async () => {
    setDatas([]);
    setIsLoading(true);
    setMessage({});
    setTotal(0);

    try {
      const {
        list = [],
        totalCount = 0,
        code,
        message
      } = await request(params);

      if (code !== RESPONSE_CODE.Success) {
        throw {
          code,
          error: message
        };
      }

      setDatas(list);
      setTotal(totalCount);
    } catch (error) {
      setMessage(error);
    } finally {
      setIsLoading(false);
    }
  };
  // 공장 목록 조회 요청
  const requestPlant = async () => {
    const { list = [] } = await factory();

    setPlants(list);
  };

  /* event */
  // 검색 클릭 시
  const onSearchClick = event => {
    event.preventDefault();

    setParams({
      ...params,
      factoryCodeSeq,
      haccpAuth,
      useYn,
      userId,
      userNm,
      page: 0
    });
  };

  // 등록/수정 클릭 시
  const onSettingClick = seq => {
    history.push(
      format(ROUTER_PATH.sfsh.standard.employee.setting, { seq }),
      params
    );
  };

  /* 엑셀다운로드 */
  const onExcelDown = async () => {
    const { factoryCodeSeq, haccpAuth, useYn, userId, userNm } = params || {};
    const requestParams = {
      factoryCodeSeq: factoryCodeSeq,
      haccpAuth: haccpAuth,
      useYn: useYn,
      userId: userId,
      userNm: userNm
    };

    const { list = [], totalCount = 0, code } = await request(requestParams);

    if (code !== RESPONSE_CODE.Success) return;

    let excelNumber = totalCount;

    downloadExcelByJSON(
      datas2SfshExcelJSON(
        list?.map(e => ({
          ...e,
          no: excelNumber--,
          authList: clean(
            pluck(e.authList, 'pltCode').map(
              value =>
                plants.find(({ pltCode } = {}) => value === pltCode)?.pltNmKo
            )
          ).join(', '),
          haccpAuth: HACCP_AUTH_TYPE_SELECT[e.haccpAuth],
          loginDt: utc2tz(e.loginDt),
          regDt: utc2tz(e.regDt, 'YYYY-MM-DD')
        })),
        excelColumns
      ),
      `${title}.xlsx`
    );
  };

  /* view */
  return (
    <ListInContent
      className={{ body: 'pt-0' }}
      columns={columns.map(text => ({
        text,
        className: 'text-nowrap'
      }))}
      message={message}
      showLoading={isLoading}
    >
      <Fragment key="title">{title}</Fragment>
      <Fragment key="headerButton">
        <Button
          size="sm"
          variant="secondary"
          onClick={onExcelDown}
          disabled={total === 0}
        >
          엑셀 다운로드
        </Button>
        <Button className="ms-2" size="sm" onClick={() => onSettingClick()}>
          사원 등록
        </Button>
      </Fragment>
      <Fragment key="search">
        <Row className="ms-0 me-0 mt-3">
          {/* 공장 선택 select-box */}
          <Col sm={2} className="ps-1 pe-1">
            <Select
              datas={
                haccpAuth === HACCP_AUTH_TYPE.ADMIN
                  ? []
                  : array2ValueAndText(plants, 'seq', 'pltNmKo')
              }
              placeholder="담당 공장 선택"
              showAll
              value={factoryCodeSeq}
              className="w-100"
              onChange={({ target: { value } }) => setFactoryCodeSeq(value)}
            />
          </Col>
          {/* 구분 선택 select-box */}
          <Col sm={2} className="ps-1 pe-1">
            <Select
              datas={Object.entries(HACCP_AUTH_TYPE_SELECT).map(
                ([value, text]) => ({
                  value,
                  text
                })
              )}
              placeholder="결재권한 선택"
              showAll
              value={haccpAuth}
              className="w-100"
              onChange={({ target: { value } }) => {
                setHaccpAuth(numberFrom(value, value));
              }}
            />
          </Col>
          {/* 사용여부 select-box */}
          <Col sm={2} className="ps-1 pe-1">
            <Select
              datas={array2ValueAndText(USE_YN_SELECT, 'key', 'value')}
              placeholder="사용유무 선택"
              showAll
              value={useYn}
              className="w-100"
              onChange={({ target: { value } }) => setUseYn(value)}
            />
          </Col>
          <Col sm={4} className="ps-1 pe-1">
            <InputGroup size="sm" className="w-100">
              <Select
                datas={[
                  {
                    text: 'ID',
                    value: 0
                  },
                  {
                    text: '담당자명',
                    value: 1
                  }
                ]}
                placeholder="구분 선택"
                value={type}
                className="w-50"
                onChange={({ target: { value } }) => setType(value)}
              />
              <Form.Control
                size="sm"
                type="text"
                value={[userId, userNm][type]}
                autoFocus
                className="w-50"
                placeholder="입력해주세요."
                onChange={({ target: { value } }) =>
                  [setUserId, setUserNm][type](value)
                }
              />
            </InputGroup>
          </Col>
          <Col
            sm="auto"
            className="ps-1 pe-1 d-flex flex-row align-items-center"
          >
            <Button disabled={isLoading} size="sm" onClick={onSearchClick}>
              조회
            </Button>
            <p className="mb-0 ms-3 fs--1 fw-bold lh-1">
              검색 결과 : {comma(total)} 건
            </p>
          </Col>
        </Row>
      </Fragment>
      <Fragment key="datas">
        {datas.map(
          (
            {
              seq,
              userId,
              userNm,
              authList = [],
              haccpAuth,
              useYn,
              loginDt,
              regDt
            } = {},
            index
          ) => (
            <tr
              key={`datas${seq}`}
              onClick={() => onSettingClick(seq)}
              className="cursor-pointer"
            >
              <td>{total - params.size * params.page - index}</td>
              <td>{userId}</td>
              <td>{userNm}</td>
              <td>
                {clean(
                  pluck(authList, 'pltCode').map(
                    value =>
                      plants.find(({ pltCode } = {}) => value === pltCode)
                        ?.pltNmKo
                  )
                ).join(', ')}
              </td>
              <td>{HACCP_AUTH_TYPE_SELECT[haccpAuth]}</td>
              <td>{useYn}</td>
              <td>{utc2tz(loginDt)}</td>
              <td>{utc2tz(regDt, 'YYYY-MM-DD')}</td>
            </tr>
          )
        )}
      </Fragment>
      <Fragment key="pagination">
        {total > 0 && (
          <Pagination
            currentPage={params.page + 1}
            perPage={params.size}
            length={10}
            totalCount={total}
            movePage={page => setParams({ ...params, page: page - 1 })}
          />
        )}
      </Fragment>
    </ListInContent>
  );
};

export default List;
